import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { PregnantSelection, ScreenedOutReason } from "@/constants";
import { SurveyAnswer } from "@/contexts";
import { isCmsAccessEnabled } from "@/utils/partners";

import {
  QuestionComponentProps,
  QuestionDefinition,
  QuestionOption,
} from "../../Question";
import { MultipleChoiceQuestion } from "../core";

const QUESTION_ID = "pregnant";

declare module "@/contexts" {
  interface SurveyAnswers {
    pregnant?: PregnantSelection;
  }
}

const PregnantQuestion: React.FC<QuestionComponentProps> = ({
  onClickBack,
  onClickNext,
  surveyAnswers,
}) => {
  const [shouldShowConfirmation, setShouldShowConfirmation] = useState(false);
  const [selectedItem, setSelectedItem] = useState(
    surveyAnswers[QUESTION_ID] || "",
  );
  const { t } = useTranslation();

  const {
    liabilityWaiverBMI,
    liabilityWaiverEatingDisorder,
    liabilityWaiverIdealWeight,
  } = surveyAnswers;
  const hasAlreadySignedLiabilityWaiver =
    !!liabilityWaiverEatingDisorder ||
    !!liabilityWaiverBMI ||
    !!liabilityWaiverIdealWeight;

  const options: QuestionOption[] = [
    { id: PregnantSelection.YES, text: t("pregnant.options.yes") },
    // In the future, we may want to include this option for weight loss pacing calculation
    // Currently, this question appears after pacing so it isn't useful.
    {
      id: PregnantSelection.NO_BUT_NURSING,
      text: t("pregnant.options.noButNursing"),
    },
    {
      id: PregnantSelection.NO_BUT_PLANNING,
      text: t("pregnant.options.noButPlanning"),
    },
    { id: PregnantSelection.NO, text: t("pregnant.options.no") },
  ];

  const handleAnswerBeforeProceeding = async (
    answer: SurveyAnswer,
    _additionalAnswers?: Record<string, SurveyAnswer>,
    _screenedOutReason?: ScreenedOutReason,
    source?: "button" | "option",
  ) => {
    setSelectedItem(answer as string);

    if (source === "button") {
      onClickNext(answer);
      return;
    }

    if (shouldShowConfirmation) {
      return;
    }

    if (
      answer === PregnantSelection.YES &&
      !hasAlreadySignedLiabilityWaiver &&
      !shouldShowConfirmation
    ) {
      setShouldShowConfirmation(true);
      return;
    }

    onClickNext(answer);
  };

  return (
    <MultipleChoiceQuestion
      confirmationElement={
        shouldShowConfirmation ? (
          <Trans i18nKey="pregnant.confirmation" />
        ) : undefined
      }
      ctaText={shouldShowConfirmation ? t("pregnant.ctaText") : ""}
      onClickBack={onClickBack}
      onClickNext={handleAnswerBeforeProceeding}
      options={options}
      questionId={QUESTION_ID}
      questionText={t("pregnant.questionText")}
      selectedItem={selectedItem}
    />
  );
};

export const Q_PREGNANT: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["WEIGHT", "DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  // CMS ACCESS members already answered this on the ACCESS screen.
  shouldShowQuestion: ({ partnerInfo, surveyAnswers: { sex } }) =>
    !isCmsAccessEnabled(partnerInfo) &&
    (sex === "female" || sex === "intersex" || sex === "preferNotToSay"),
  component: PregnantQuestion,
};

export const Q_RETRIAGE_PREGNANT: QuestionDefinition = {
  id: QUESTION_ID,
  supportedPrograms: ["DPP", "DIABETES_LIFESTYLE", "MED", "NMPBA"],
  shouldShowQuestion: ({ surveyAnswers: { sex } }) =>
    sex === "female" || sex === "intersex" || sex === "preferNotToSay",
  component: PregnantQuestion,
};
